import { IconHeartOff } from '@tabler/icons-react-native'
import { ComponentRef, useCallback, useRef, type FC } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type ProfileUnmatchButton_match$key } from '@/__generated__/ProfileUnmatchButton_match.graphql'
import { Dialog, ProfileButton, type ProfileButtonProps } from '@/components'
import { useDisclosure, useUnmatchMutation } from '@/hooks'

export interface ProfileUnmatchButtonProps extends Omit<ProfileButtonProps, 'label' | 'onPress'> {
  label?: string
  onConfirm?: () => void
  matchRef: ProfileUnmatchButton_match$key | null | undefined
}

export const ProfileUnmatchButton: FC<ProfileUnmatchButtonProps> = ({
  isDisabled: _isDisabled,
  label = 'Unmatch',
  onConfirm,
  matchRef,
  ...props
}) => {
  const buttonRef = useRef<ComponentRef<typeof ProfileButton>>(null)
  const matchData = useFragment(
    graphql`
      fragment ProfileUnmatchButton_match on Match {
        profile {
          name
        }
        rowId
      }
    `,
    matchRef
  )
  const [unmatch, isUnmatching] = useUnmatchMutation()
  const [showUnmatchDialog, unmatchDialog] = useDisclosure(false)
  const isDisabled = _isDisabled || isUnmatching

  return (
    <>
      <ProfileButton
        accessibilityLabel={`Unmatch ${matchData?.profile?.name ?? 'this profile'}`}
        Icon={IconHeartOff}
        isDisabled={isDisabled}
        label={label}
        onPress={unmatchDialog.open}
        ref={buttonRef}
        {...props}
      />
      <Dialog
        finalFocusRef={buttonRef}
        isDisabled={isDisabled}
        isOpen={showUnmatchDialog}
        onClose={unmatchDialog.close}
        onConfirm={useCallback(async () => {
          await unmatch(matchData?.rowId)
          onConfirm?.()
        }, [unmatch, onConfirm, matchData?.rowId])}
        title={`Unmatch ${matchData?.profile?.name ?? 'Profile'}`}
      >
        {`Are you sure you want to unmatch ${matchData?.profile?.name ?? 'this profile'}?`}
      </Dialog>
    </>
  )
}
